import { InputSignal, ModelSignal, OutputEmitterRef, Type, WritableSignal } from '@angular/core';
import { ColorPickerComponent, ParameterValueComponent } from '@vectronic/ui';
import {
  DataForwardingChipsCellComponent,
  DatatableAutocompleteInputComponent,
  DatatableBeltComponent,
  DatatableCheckboxComponent,
  DatatableCommandContentComponent,
  DatatableDateWithRelativeWeeksComponent,
  DatatableDisabledSubsystemsComponent,
  DatatableDropOffComponent,
  DatatableDropOffStringComponent,
  DatatableEditCheckboxComponent,
  DatatableFirmwareComponent,
  DatatableGroupsCheckboxComponent,
  DatatableIdsTooltipComponent,
  DatatableInputComponent,
  DatatableJsonComponent,
  DatatableListWithActionsComponent,
  DatatableMovebankSwitchComponent,
  DatatableMultiValuesContentComponent,
  DatatableParameterComponent,
  DatatableRmaDescriptionComponent,
  DatatableRmaDeviceSelectorComponent,
  DatatableSchedulesComponent,
  DatatableStatusSelectComponent,
  DatatableStringWithActionComponent,
  DatatableSvgComponent,
  DatatableUsersCountComponent,
  GroupChipsCellComponent,
  MemberChipsCellComponent,
  MovebankForwardingChipsCellComponent,
  MultiRoleSelectorComponent,
  NotificationChipsCellComponent,
  RoleSelectorComponent,
} from '../column-components';

export type DisplayColumn<T extends object> = DatatableColumn<T>['fieldName'] | 'rowActions';

type Keys<T extends object> =
  | '#'
  | Key<T>
  | `${Key<T>}.lmt`
  | `${Key<T>}.desktop`
  | `${Key<T> & 'parameterDataList'}.${string}`
  | `${Key<T>},${Key<T>}`
  | PathKeys<T>;

type Key<T extends object> = keyof T & string;

type PathKeys<T extends object> = {
  [K in keyof T & string]: NonNullable<T[K]> extends infer U
    ? U extends object
      ? U extends any[] | ((...args: any[]) => any)
        ? `${K}.${keyof U & string}`
        : `${K}.${keyof U & string}` | `${K}.${SubPathKeys<U>}`
      : never
    : never;
}[keyof T & string];

type SubPathKeys<T extends object> = {
  [K in keyof T & string]: NonNullable<T[K]> extends infer U
    ? U extends object
      ? `${K}.${keyof U & string}`
      : never
    : never;
}[keyof T & string];

export type DatatableColumnIcon<T extends object> = {
  matIcon: string;
  onClick?: (row: T, event: MouseEvent) => void;
  doHide?: (row: T) => boolean;
  doDisable?: (row: T) => boolean;
  color?: string;
  hoverText?: (row: T) => string;
};

export type DatatableColumn<T extends object> =
  | _DatatableColumn<T, never>
  | {
      [K in keyof ColumnComponentTypeMap]: _DatatableColumn<T, ColumnComponentTypeMap[K]>;
    }[keyof ColumnComponentTypeMap];

type _DatatableColumn<T extends object, C extends ColumnComponent = never> = {
  fieldName: Keys<T>;
  columnId: number; //NOTE this is how the column visibility/order is saved. changing this messes with the saves
  headerName?: string; //NOTE defaults to fieldName capitalized and with spaces
  translator?: (row: T) => any;
  secondaryText?: (row: T) => { text: string; style?: any } | undefined;
  component?: {
    name: NameOfColumnComponent<C>;
    input?: ComponentInputs<InstanceType<C>, T>;
    output?: ComponentOutputs<InstanceType<C>, T>;
    doNotUseColumnComponent?: (row: T) => boolean;
  };
  hide?: boolean;
  headerHoverText?: string;
  trim?: number; //NOTE if string is longer it gets cut to given character length, cellHovertext is added automatically
  ellipsis?: boolean; //NOTE if true, uses CSS ellipsis based on the actual column width
  cellHoverText?: (row: T) => string;
  cellHoverComponent?: (row: T) => Type<{ row: InputSignal<any> }>;
  editable?: (row: T) => boolean; //NOTE makes the string field editable, need to handle edit with onEdit
  onEdit?: (row: T, editedValue: string) => void; //NOTE triggers when row is editable and edited
  editDropdown?: (row: T) => (string | number)[]; //NOTE if provided, showing a dropdown when editable is true
  onEditDropdown?: (row: T, value: string | number) => void;
  disableSort?: boolean; //NOTE defaults to false like all optional booleans
  sortStart?: 'asc' | 'desc'; //NOTE controls the direction used on the first sort click
  cellStyle?: (row: T) => any; //NOTE example: (row) => row.alert ? { 'background-color': 'red' } : { 'font-style': 'italic' }
  sticky?: 'start' | 'startAfterCheckbox' | 'end'; //NOTE use startAfterCheckbox only, if a checkbox should be stick:start, and a second column should be sticky after this checkbox
  compactModeSize?: number; //NOTE compactmode/monospaced fonts, adds empty space to the left. example size 5: 111.1 , _ 11.1, _ _ 1.1 are in one line below eachother
  compactModeFloatNumber?: boolean; //NOTE compactModeSize only applies to the integer part, so different amount of decimals are accounted for
  normalModeSize?: number; //NOTE the same like compactModeSize, but for non-compact-mode
  icons?: DatatableColumnIcon<T>[];
  onClick?: (row: T) => void; //NOTE can be used on the default column, type == undefined
  hideInCompactMode?: boolean;
  fixWidthInPx?: number; //NOTE could be used to reduce column jumps when scrolling
  disableCsvExport?: boolean; //NOTE ignores this column in the exported csv
  csvHeader?: string; //NOTE defaults to headerName
  csvTranslator?: (row: T) => string; //NOTE in case a different translator is needed for the csv
  sortValue?: (row: T) => number | string; //NOTE used if the value is not sorted with standard compare. eg: Dates are not sorted alphabetically but by .getTime
  searchString?: (row: T) => string; //NOTE used when there is no string/number value but the column should still be searchable
  disableStandardSearch?: boolean; //NOTE ignores the column when using the searchfield
  isLoading?: (row: T) => boolean; //NOTE adds a loading symbol
  loadingProgress?: (row: T) => number | undefined;
  tooltipClass?: string;
  toggle?: {
    onToggle: (row: T, checked: boolean) => void;
    hoverText?: (row: T) => string;
    isDisabled?: (row: T) => boolean;
    doHide?: (row: T) => boolean;
  };
  //NOTE csvImport fields:
  csvImport?: 'required' | 'optional';
  parser?: (cellContent: string) => any;
  gpsXHeader?: string;
  gpsXTimeHeader?: string;
  gpsXParser?: (cellContent: string) => any;
  gpsXDateParser?: (date: string, time: string) => string;
  allowBreaks?: boolean; // if true, this will use style white-space: pre-line to header + default column, type == undefined
  allowSpaces?: boolean; // if true, this will use style white-space: pre to header + default column, type == undefined
};

export const componentsThatEmitOnClick: NameOfColumnComponent<ColumnComponent>[] = [
  'DatatableBeltComponent',
  'DatatableDropOffComponent',
  'DatatableDropOffStringComponent',
  'DatatableCommandContentComponent',
  'DatatableMultiValuesContentComponent',
  'DatatableFirmwareComponent',
  'DatatableListWithActionsComponent',
];

type ColumnComponentTypeMap = {
  DatatableAutocompleteInputComponent: typeof DatatableAutocompleteInputComponent;
  DatatableCheckboxComponent: typeof DatatableCheckboxComponent;
  DatatableUsersCountComponent: typeof DatatableUsersCountComponent;
  DatatableIdsTooltipComponent: typeof DatatableIdsTooltipComponent;
  DatatableStatusSelectComponent: typeof DatatableStatusSelectComponent;
  DatatableGroupsCheckboxComponent: typeof DatatableGroupsCheckboxComponent;
  DatatableBeltComponent: typeof DatatableBeltComponent;
  DatatableDropOffComponent: typeof DatatableDropOffComponent;
  DatatableDropOffStringComponent: typeof DatatableDropOffStringComponent;
  DatatableMovebankSwitchComponent: typeof DatatableMovebankSwitchComponent;
  ColorPickerComponent: typeof ColorPickerComponent;
  RoleSelectorComponent: typeof RoleSelectorComponent;
  MultiRoleSelectorComponent: typeof MultiRoleSelectorComponent;
  DatatableListWithActionsComponent: typeof DatatableListWithActionsComponent;
  GroupChipsCellComponent: typeof GroupChipsCellComponent;
  MemberChipsCellComponent: typeof MemberChipsCellComponent;
  NotificationChipsCellComponent: typeof NotificationChipsCellComponent;
  DataForwardingChipsCellComponent: typeof DataForwardingChipsCellComponent;
  MovebankForwardingChipsCellComponent: typeof MovebankForwardingChipsCellComponent;
  DatatableJsonComponent: typeof DatatableJsonComponent;
  DatatableParameterComponent: typeof DatatableParameterComponent;
  DatatableDisabledSubsystemsComponent: typeof DatatableDisabledSubsystemsComponent;
  DatatableSchedulesComponent: typeof DatatableSchedulesComponent;
  DatatableStringWithActionComponent: typeof DatatableStringWithActionComponent;
  DatatableCommandContentComponent: typeof DatatableCommandContentComponent;
  DatatableMultiValuesContentComponent: typeof DatatableMultiValuesContentComponent;
  DatatableSvgComponent: typeof DatatableSvgComponent;
  DatatableFirmwareComponent: typeof DatatableFirmwareComponent;
  DatatableEditCheckboxComponent: typeof DatatableEditCheckboxComponent;
  DatatableDateWithRelativeWeeksComponent: typeof DatatableDateWithRelativeWeeksComponent;
  ParameterValueComponent: typeof ParameterValueComponent;
  DatatableRmaDeviceSelectorComponent: typeof DatatableRmaDeviceSelectorComponent;
  DatatableRmaDescriptionComponent: typeof DatatableRmaDescriptionComponent;
  DatatableInputComponent: typeof DatatableInputComponent;
};

type ColumnComponent = ColumnComponentTypeMap[keyof ColumnComponentTypeMap];

type NameOfColumnComponent<C> = {
  [K in keyof ColumnComponentTypeMap]: C extends ColumnComponentTypeMap[K] ? K : never;
}[keyof ColumnComponentTypeMap];

type ComponentInputs<C, T extends object> = {
  [K in keyof C]?: C[K] extends ModelSignal<infer U>
    ? WritableSignal<U>
    : C[K] extends InputSignal<infer U>
      ? (row: T) => U
      : never;
};

type ComponentOutputs<C, T extends object> = {
  [K in keyof C]?: C[K] extends OutputEmitterRef<infer U> ? (event: U, row: T) => void : never;
};
